"use client";

import React from "react";
import { useState } from "react";
import CategoryItem from "~/components/detailed-stats/category-item";
import RetryGroupItem from "~/components/detailed-stats/retry-group-item";

import type {
  DetailedStatistics,
  DetailedStatisticsCategory,
} from "~/lib/detailedStats";
import { StatsCategoryType } from "~/lib/detailedStats";
import formatPercent from "~/lib/formatPercent";
// Default simple stubs used for testing. They keep the component renderable
// inside Jest (which may not transform the production Dialog implementation).
// Keep the stubs in source; they are used by the jest setup mock at test time.
// They can be exported from the jest mock so tests can re-use them if needed.
export const StubDialog: React.FC<{
  open?: boolean;
  children?: React.ReactNode;
}> = ({ open = true, children }) => {
  if (!open) return null;
  return React.createElement(React.Fragment, null, children);
};
export const StubDialogContent: React.FC<{
  children?: React.ReactNode;
  showCloseButton?: boolean;
}> = ({ children }) => React.createElement("div", null, children);
export const StubDialogHeader: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => React.createElement("div", null, children);
export const StubDialogTitle: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => React.createElement("div", null, children);

// In code runtime we use the actual Dialog primitives provided by the app.
// Tests will mock this module via `jest.mock('~/components/ui/dialog')`.
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
// Note: `Switch` is used inside `StatsControls` (extracted component).
import StatsControls from "~/components/detailed-stats/stats-controls";
import StatsSummary from "~/components/detailed-stats/stats-summary";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  statistics: DetailedStatistics;
  problemSetId: number | string;
  percentAll?: number;
  percentSolved?: number;
  earnedPointsAll?: number;
  maxPointsAll?: number;
  earnedPointsSolved?: number;
  maxPointsSolved?: number;
  scurr?: number;
  smax?: number;
  smaxSolved?: number;
  scurrSolved?: number;
  hasSolvedProblems?: boolean;
};

export default function DetailedStatsDialog({
  isOpen,
  onClose,
  statistics,
  problemSetId,
  percentAll = 0,
  percentSolved = 0,
  earnedPointsAll,
  maxPointsAll,
  earnedPointsSolved,
  maxPointsSolved,
  scurr = 0,
  smax = 0,
  smaxSolved = 0,
  scurrSolved = scurr,
  hasSolvedProblems,
}: Props) {
  const [isFullMode, setIsFullMode] = useState(false);
  const [isSolvedOnlyMode, setIsSolvedOnlyMode] = useState(false);
  // Retry group expansion is handled within `RetryGroupItem` locally.

  const isSolvedCategory = (type: StatsCategoryType): boolean => {
    return (
      type === StatsCategoryType.WIN ||
      type === StatsCategoryType.DRAW ||
      type === StatsCategoryType.EXTERNAL_HINT
    );
  };
  const computedSolvedCount = (statistics?.categories ?? []).reduce(
    (sum: number, category: DetailedStatisticsCategory) => {
      if (category.type === StatsCategoryType.RETRY_GROUP) {
        return (
          sum +
          (category.subCategories ?? []).reduce(
            (subSum: number, subCategory: DetailedStatisticsCategory) => {
              return isSolvedCategory(subCategory.type)
                ? subSum + subCategory.count
                : subSum;
            },
            0,
          )
        );
      }

      return isSolvedCategory(category.type) ? sum + category.count : sum;
    },
    0,
  );
  const solvedCount = statistics?.solvedCount ?? computedSolvedCount;
  const denominator = isSolvedOnlyMode
    ? Math.max(solvedCount, 1)
    : Math.max(statistics?.totalCount ?? 1, 1);
  const uncompletedCount = statistics?.uncompletedCount ?? 0;
  const hasSolvedProblemsValue = hasSolvedProblems ?? solvedCount > 0;
  const earnedPointsAllValue =
    statistics?.earnedPointsAll ?? earnedPointsAll ?? scurr ?? 0;
  const maxPointsAllValue =
    statistics?.maxPointsAll ?? maxPointsAll ?? smax ?? 0;
  const earnedPointsSolvedValue =
    statistics?.earnedPointsSolved ??
    earnedPointsSolved ??
    scurrSolved ??
    earnedPointsAllValue;
  const maxPointsSolvedValue =
    statistics?.maxPointsSolved ?? maxPointsSolved ?? smaxSolved ?? maxPointsAllValue;

  const visibleCategories = (() => {
    const categories = statistics?.categories ?? [];
    if (!isSolvedOnlyMode) return categories;

    return categories.flatMap((category: DetailedStatisticsCategory) => {
      if (category.type === StatsCategoryType.RETRY_GROUP) {
        const visibleSubCategories = (category.subCategories ?? []).filter(
          (subCategory: DetailedStatisticsCategory) =>
            isSolvedCategory(subCategory.type),
        );

        if (visibleSubCategories.length === 0) return [];

        return [
          {
            ...category,
            count: visibleSubCategories.reduce(
              (sum, subCategory) => sum + subCategory.count,
              0,
            ),
            earnedPoints: visibleSubCategories.reduce(
              (sum, subCategory) => sum + (subCategory.earnedPoints ?? 0),
              0,
            ),
            maxPoints: visibleSubCategories.reduce(
              (sum, subCategory) => sum + (subCategory.maxPoints ?? 0),
              0,
            ),
            tasks: visibleSubCategories.flatMap(
              (subCategory) => subCategory.tasks ?? [],
            ),
            subCategories: visibleSubCategories,
          } satisfies DetailedStatisticsCategory,
        ];
      }

      return isSolvedCategory(category.type) ? [category] : [];
    });
  })();

  const categoryItems = visibleCategories.length
    ? visibleCategories.map(
        (cat: DetailedStatisticsCategory, idx: number) => {
          // Special rendering for RETRY_GROUP categories: clickable row that expands
          if (cat.type === StatsCategoryType.RETRY_GROUP) {
            return (
              <RetryGroupItem
                key={`cat-${idx}`}
                category={cat}
                denominator={denominator}
                isFullMode={isFullMode}
                problemSetId={problemSetId}
                onClose={onClose}
                idx={idx}
              />
            );
          }

          // Default rendering for non-group categories
          return (
            <CategoryItem
              key={`cat-${idx}`}
              category={cat}
              denominator={denominator}
              isFullMode={isFullMode}
              problemSetId={problemSetId}
              onClose={onClose}
            />
          );
        },
      )
    : undefined;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(v: boolean) => {
        if (!v) onClose();
      }}
    >
      <DialogContent showCloseButton={false} className="max-w-4xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Статистика для набора задач</DialogTitle>
            <StatsControls
              isFullMode={isFullMode}
              setIsFullMode={setIsFullMode}
              isSolvedOnlyMode={isSolvedOnlyMode}
              setIsSolvedOnlyMode={setIsSolvedOnlyMode}
            />
          </div>
        </DialogHeader>
        <div className="mt-2 max-h-[80vh] w-full overflow-y-auto">
          <StatsSummary
            totalCount={statistics?.totalCount ?? 0}
            uncompletedCount={uncompletedCount}
            isSolvedOnlyMode={isSolvedOnlyMode}
            denominator={denominator}
            percentAll={percentAll}
            percentSolved={percentSolved}
            earnedPointsAll={earnedPointsAllValue}
            maxPointsAll={maxPointsAllValue}
            earnedPointsSolved={earnedPointsSolvedValue}
            maxPointsSolved={maxPointsSolvedValue}
            scurr={scurr}
            smax={smax}
            smaxSolved={smaxSolved}
            scurrSolved={scurrSolved}
            hasSolvedProblems={hasSolvedProblemsValue}
          />

          {categoryItems?.length ? (
            categoryItems
          ) : (
            <div className="text-sm text-muted-foreground">
              Нет подробной статистики
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
